import { z } from 'zod';

import { SCREAMING_SNAKE_CASE_RE } from '../_shared/codes';
import { AppUserIdSchema } from '../user/user';

export const SquadIdSchema = z.guid().brand<'SquadId'>();

export type SquadId = z.infer<typeof SquadIdSchema>;

export const SquadCodeSchema = z
  .string()
  .regex(SCREAMING_SNAKE_CASE_RE, 'Code must be SCREAMING_SNAKE_CASE.')
  .brand<'SquadCode'>();

export type SquadCode = z.infer<typeof SquadCodeSchema>;

export const SquadSchema = z.object({
  id: SquadIdSchema,
  code: SquadCodeSchema,
  name: z.string(),
});

export type Squad = z.infer<typeof SquadSchema>;

export const ProjectIdSchema = z.guid().brand<'ProjectId'>();

export type ProjectId = z.infer<typeof ProjectIdSchema>;

export const ProjectCodeSchema = z
  .string()
  .regex(SCREAMING_SNAKE_CASE_RE, 'Code must be SCREAMING_SNAKE_CASE.')
  .brand<'ProjectCode'>();

export type ProjectCode = z.infer<typeof ProjectCodeSchema>;

export const ProjectSchema = z.object({
  id: ProjectIdSchema,
  code: ProjectCodeSchema,
  name: z.string(),
  icon: z.string(),
  description: z.string().optional(),
  /**
   * The user who owns this project. Immutable from the
   * `/project-members/:userId` endpoints — the owner cannot have their
   * role changed or be removed (POST/PATCH/DELETE refuse with 403).
   * Set at project creation to the caller's appUserId; backfilled for
   * pre-existing projects via the Authz Fase 8 migration.
   */
  ownerUserId: AppUserIdSchema,
  squads: z.array(SquadSchema),
  /**
   * Caller's effective RES-scoped permissions on this project, as
   * computed by kanban-api (`computeProjectPermissions`). Drives
   * frontend conditional rendering; backend stays the source of truth
   * — every mutation can still 403. Strings on the wire so this schema
   * has no dependency on `voll-authz`; consumers (web) narrow to the
   * `Permission` union at the read seam.
   */
  permissions: z.array(z.string()).default([]),
});

export type Project = z.infer<typeof ProjectSchema>;

export const CreateProjectInputSchema = z
  .object({
    code: ProjectCodeSchema,
    name: z.string().trim().min(1),
    icon: z.string().min(1),
    description: z.string().optional(),
  })
  .strict();

export type CreateProjectInput = z.infer<typeof CreateProjectInputSchema>;

export const UpdateProjectInputSchema = z
  .object({
    name: z.string().trim().min(1).optional(),
    icon: z.string().min(1).optional(),
    description: z.string().trim().optional(),
  })
  .strict();

export type UpdateProjectInput = z.infer<typeof UpdateProjectInputSchema>;

export const CreateSquadInputSchema = z
  .object({
    code: SquadCodeSchema,
    name: z.string().trim().min(1),
  })
  .strict();

export type CreateSquadInput = z.infer<typeof CreateSquadInputSchema>;

export const UpdateSquadInputSchema = z
  .object({
    name: z.string().trim().min(1).optional(),
  })
  .strict();

export type UpdateSquadInput = z.infer<typeof UpdateSquadInputSchema>;
